/**
 * Ligação de dados — do JSON para os campos da tela.
 *
 * Funções puras, sem Angular e sem estado. O serviço antigo
 * (`data-binding.service.ts`, 690 linhas) guarda o dado num `BehaviorSubject` e
 * expõe `setData`/`getCurrentData`, o que cria uma segunda fonte de verdade ao
 * lado do formulário: dois lugares guardando o mesmo valor, e nenhum deles
 * claramente o dono. Aqui o dono é o `FormGroup`, e este arquivo só o preenche.
 *
 * A semântica de `resolvePath` foi mantida IGUAL à do serviço antigo, inclusive
 * um detalhe que parece bobagem e não é: **nunca devolver `undefined`, sempre
 * `null`**. O comentário original explica por quê — `nz-input-number` chama
 * `.toString()` no valor recebido e estoura com `undefined`. Mudar isso aqui
 * reintroduziria um bug que alguém já pagou para descobrir.
 */
import { ScreenDocument } from '../model/screen-schema';
import { ScreenNode } from '../model/node-props';
import { ScreenForm } from './screen-form.service';

/**
 * Lê um caminho no dado, aceitando ponto e índice de array.
 *
 * Ex.: `'order.items[0].product.name'`.
 *
 * Devolve `null` — nunca `undefined` — quando o caminho não leva a lugar nenhum.
 */
export function resolvePath(data: unknown, path: string): unknown {
  if (data === null || data === undefined) {
    return null;
  }
  if (path === null || path === undefined || path.length === 0) {
    return null;
  }

  const parts = path.split(/[.[\]]/).filter(part => part.length > 0);
  let current: unknown = data;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return null;
    }
    if (typeof current !== 'object') {
      return null;
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (current === undefined) {
    return null;
  }
  return current;
}

/** As transformações que o modelo antigo oferecia, sem nenhuma a mais. */
export type TransformName =
  | 'uppercase' | 'lowercase'
  | 'currency' | 'date' | 'datetime' | 'percent' | 'number';

/**
 * Aplica uma transformação declarada em `binding.transform`.
 *
 * Nome desconhecido devolve o valor intocado, e não lança: a transformação vem
 * do banco, e uma tela publicada não pode quebrar porque alguém digitou
 * `curency` no editor de JSON.
 */
export function applyTransform(value: unknown, transform: string | undefined): unknown {
  if (transform === undefined || transform.length === 0) {
    return value;
  }
  if (value === null || value === undefined) {
    return value;
  }

  const name = transform.toLowerCase();

  if (name === 'uppercase') {
    return String(value).toUpperCase();
  }
  if (name === 'lowercase') {
    return String(value).toLowerCase();
  }
  if (name === 'currency') {
    return formatCurrency(value);
  }
  if (name === 'date') {
    return formatDate(value, false);
  }
  if (name === 'datetime') {
    return formatDate(value, true);
  }
  if (name === 'percent') {
    return formatPercent(value);
  }
  if (name === 'number') {
    return formatNumber(value);
  }

  return value;
}

export function resolveWithTransform(
  data: unknown,
  path: string,
  transform?: string
): unknown {
  return applyTransform(resolvePath(data, path), transform);
}

// ---------------------------------------------------------------- formatação

function toFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
}

function formatCurrency(value: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return String(value);
  }
  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatNumber(value: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return String(value);
  }
  return numeric.toLocaleString('pt-BR');
}

function formatPercent(value: unknown): string {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return String(value);
  }
  return numeric.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + '%';
}

function formatDate(value: unknown, withTime: boolean): string {
  const parsed = toDate(value);
  if (parsed === null) {
    return String(value);
  }

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  const date = day + '/' + month + '/' + year;

  if (!withTime) {
    return date;
  }

  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return date + ' ' + hours + ':' + minutes;
}

/**
 * Aceita `Date`, texto de data e epoch.
 *
 * O epoch entra em segundos E em milissegundos porque os dois aparecem em
 * produção: `update_time: 1722629279` (segundos, Shopee) é o caso que a seção
 * 7.2 do plano cita. Dez dígitos são segundos; treze, milissegundos.
 */
function toDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const asMilliseconds = value < 1e11 ? value * 1000 : value;
    const parsed = new Date(asMilliseconds);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'string' && value.length > 0) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

// ---------------------------------------------------------------- aplicação

/** Um campo que recebeu valor, para o relatório de aplicação. */
export interface BoundField {
  readonly nodeId: string;
  readonly dataPath: string;
  readonly value: unknown;
}

export interface BindingResult {
  readonly applied: readonly BoundField[];
  /** Caminhos que não existem no dado recebido. */
  readonly missingPaths: readonly string[];
}

/**
 * Preenche o formulário a partir do dado.
 *
 * Escreve com `emitEvent: false`, pela mesma razão de `ScreenFormService.writeValue`:
 * carregar a tela não pode disparar o `onChange` de cada campo, senão abrir uma
 * tela executaria toda a lógica dela como se o usuário tivesse digitado tudo.
 *
 * `missingPaths` não é enfeite: é o insumo da tela "Verificar tela" para o aviso
 * de `dataPath` inexistente no JSON de amostra (seção 10 do plano).
 */
export function applyDataToForm(
  document: ScreenDocument,
  screenForm: ScreenForm,
  data: unknown
): BindingResult {

  const applied: BoundField[] = [];
  const missingPaths: string[] = [];

  const visit = (node: ScreenNode): void => {
    bindOne(node, screenForm, data, applied, missingPaths);

    if (node.children !== undefined) {
      for (const child of node.children) {
        visit(child);
      }
    }
  };

  visit(document.root);

  return { applied, missingPaths };
}

function bindOne(
  node: ScreenNode,
  screenForm: ScreenForm,
  data: unknown,
  applied: BoundField[],
  missingPaths: string[]
): void {

  const declarado = node.binding?.dataPath;

  // Sem caminho declarado, o id do no serve de chave. NAO e mudanca de
  // comportamento em producao: hoje um no sem `dataPath` nunca e preenchido por
  // ninguem, entao a reserva so torna preenchivel o que era impreenchivel — e e
  // o que faz o dado de exemplo (e uma tela montada a mao) funcionar.
  const dataPath = declarado === undefined || declarado.length === 0
    ? node.id
    : declarado;

  const controlName = screenForm.controlByNodeId.get(node.id);
  if (controlName === undefined) {
    // Container ou componente estático: tem caminho, mas não guarda valor.
    // A grade é o caso comum — ela lê o array pelo próprio caminho.
    return;
  }

  const raw = resolvePath(data, dataPath);

  if (raw === null) {
    // So caminho DECLARADO conta como ausente. A reserva pelo id preenche
    // quando encontra e fica calada quando nao encontra — reportar faria todo
    // campo sem ligacao virar "caminho ausente", e o diagnostico cheio de falso
    // e um diagnostico que ninguem le.
    if (declarado !== undefined && declarado.length > 0) {
      missingPaths.push(dataPath);
    }
    return;
  }

  const value = applyTransform(raw, node.binding?.transform);
  const control = screenForm.group.get(controlName);

  if (control === null) {
    return;
  }

  control.setValue(value, { emitEvent: false });
  applied.push({ nodeId: node.id, dataPath, value });
}
